import { resolveClient } from './supabase';
import type { MetricDefinition } from '@lingaai/types';
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Get metric definitions for a fund, optionally including portfolio company-specific metrics.
 *
 * @param fundId - The fund ID to get metrics for
 * @param portfolioCompanyId - Optional portfolio company ID to include company-specific metrics
 * @param client - Optional Supabase client
 * @returns Array of metric definitions (fund-level + company-specific if portfolioCompanyId provided)
 */
export async function getMetricDefinitions(fundId: string, portfolioCompanyId?: string | null, client?: SupabaseClient): Promise<MetricDefinition[]> {
  const supabase = resolveClient(client);

  let query = supabase.from('metric_definitions').select('*').eq('fund_id', fundId).is('archived_at', null);

  if (portfolioCompanyId) {
    // Return fund-level metrics (portfolio_company_id IS NULL)
    // AND company-specific metrics (portfolio_company_id = portfolioCompanyId)
    query = query.or(`portfolio_company_id.is.null,portfolio_company_id.eq.${portfolioCompanyId}`);
  } else if (portfolioCompanyId === null) {
    // Explicitly null means: return only fund-level metrics (portfolio_company_id IS NULL)
    query = query.is('portfolio_company_id', null);
  }
  // If portfolioCompanyId is undefined, return ALL metrics (fund-level + all company-specific)

  const { data, error } = await query
    .order('portfolio_company_id', { ascending: true, nullsFirst: true }) // Fund-level first
    .order('is_required', { ascending: false })
    .order('name', { ascending: true });

  if (error) throw error;
  return data as MetricDefinition[];
}

export async function getMetricDefinition(definitionId: string, client?: SupabaseClient): Promise<MetricDefinition> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('metric_definitions').select('*').eq('id', definitionId).single();

  if (error) throw error;
  return data as MetricDefinition;
}

export async function createMetricDefinition(
  data: {
    tenant_id: string;
    fund_id: string;
    portfolio_company_id?: string | null; // NULL = fund-level, UUID = company-specific
    name: string;
    description?: string | null;
    data_type: 'number' | 'currency' | 'percentage' | 'text';
    is_required: boolean;
    display_order: number;
  },
  client?: SupabaseClient
): Promise<MetricDefinition> {
  const supabase = resolveClient(client);

  const { data: definition, error } = await supabase.from('metric_definitions').insert(data).select().single();

  if (error) throw error;
  return definition as MetricDefinition;
}

/**
 * Get or create a metric definition - avoids duplicates by checking for existing metric with same name.
 * Used for trial uploads where the same fund may receive multiple uploads.
 *
 * @param data - The metric definition data
 * @param client - Optional Supabase client
 * @returns Existing metric definition if found, otherwise creates and returns new one
 */
export async function getOrCreateMetricDefinition(
  data: {
    tenant_id: string;
    fund_id: string;
    portfolio_company_id?: string | null;
    name: string;
    description?: string | null;
    data_type: 'number' | 'currency' | 'percentage' | 'text';
    is_required: boolean;
    display_order: number;
  },
  client?: SupabaseClient
): Promise<MetricDefinition> {
  const supabase = resolveClient(client);

  // Check for existing metric with same name in same fund (and same portfolio_company_id scope)
  let query = supabase
    .from('metric_definitions')
    .select('*')
    .eq('fund_id', data.fund_id)
    .eq('name', data.name)
    .is('archived_at', null);

  if (data.portfolio_company_id) {
    query = query.eq('portfolio_company_id', data.portfolio_company_id);
  } else {
    query = query.is('portfolio_company_id', null);
  }

  const { data: existing, error: selectError } = await query.maybeSingle();

  if (selectError) throw selectError;

  // Return existing metric if found
  if (existing) {
    return existing as MetricDefinition;
  }

  // Create new metric if not found
  const { data: definition, error: insertError } = await supabase.from('metric_definitions').insert(data).select().single();

  if (insertError) throw insertError;
  return definition as MetricDefinition;
}

export async function updateMetricDefinition(definitionId: string, updates: Partial<MetricDefinition>, client?: SupabaseClient): Promise<MetricDefinition> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('metric_definitions').update(updates).eq('id', definitionId).select().single();

  if (error) throw error;
  return data as MetricDefinition;
}

export async function archiveMetricDefinition(definitionId: string, client?: SupabaseClient): Promise<void> {
  const supabase = resolveClient(client);

  const { error } = await supabase.rpc('archive_metric_definition', {
    definition_id: definitionId,
  });

  if (error) throw error;
}
